import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { getAuth } from '../services/auth';
import Pagination from '../components/Pagination';
import {
   Plus, Edit2, Trash2, Search, Image, X, Save,
   DollarSign, Tag, Truck, Box, MoreVertical,
   Layers, Package, Check, Images, Filter,
   ZoomIn, ZoomOut, Maximize
} from 'lucide-react';
import ImageCropperModal from '../components/ImageCropperModal';

// --- CONFIGURAÇÃO DA API ---
const API_URL = 'https://zapshop.app/api';

// --- COMPONENTE: MODAL DE CATEGORIA ---
const CategoryModal = ({ tenantId, categoryToEdit, onClose, onSuccess }: any) => {
   const [name, setName] = useState('');
   const [loading, setLoading] = useState(false);

   useEffect(() => {
      if (categoryToEdit) setName(categoryToEdit.name);
      else setName('');
   }, [categoryToEdit]);

   const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setLoading(true);
      try {
         if (categoryToEdit) {
            await axios.put(`${API_URL}/categories/${categoryToEdit.id}`, { name, tenant_id: tenantId });
         } else {
            await axios.post(`${API_URL}/categories`, { tenant_id: tenantId, name });
         }
         onSuccess();
         onClose();
      } catch (error) {
         alert('Erro ao salvar categoria.');
      } finally {
         setLoading(false);
      }
   };

   return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
         <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl flex flex-col">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
               <h3 className="text-xl font-bold text-gray-800">{categoryToEdit ? 'Editar Categoria' : 'Nova Categoria'}</h3>
               <button onClick={onClose}><X className="text-gray-400 hover:text-gray-600" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
               <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Nome da Categoria</label>
                  <input required className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-emerald-500 outline-none"
                     value={name} onChange={e => setName(e.target.value)} placeholder="Ex: Perfumes" />
               </div>
               <div className="flex justify-end gap-3 pt-4">
                  <button type="button" onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg">Cancelar</button>
                  <button type="submit" disabled={loading} className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold shadow-lg shadow-emerald-600/20 flex items-center gap-2">
                     {loading ? 'Salvando...' : <><Save size={18} /> Salvar</>}
                  </button>
               </div>
            </form>
         </div>
      </div>
   );
};

// --- COMPONENTE: MODAL DE PRODUTO ---
const ProductModal = ({ tenantId, productToEdit, onClose, onSuccess }: any) => {
   const [loading, setLoading] = useState(false);
   const [categories, setCategories] = useState<any[]>([]);

   // 1. ESTADO DO FORMULÁRIO
   const [formData, setFormData] = useState({
      name: '', price: '', promotional_price: '', description: '', sku: '', stock: '', category_id: '',
      weight: '', height: '', width: '', length: '',
      allow_backorder: false
   });

   const [shippingModes, setShippingModes] = useState<string[]>(['ME_CORREIOS']);
   const [coverFile, setCoverFile] = useState<File | null>(null);
   const [galleryFiles, setGalleryFiles] = useState<File[]>([]);
   const [attributes, setAttributes] = useState<{ key: string, value: string }[]>([]);
   const [variations, setVariations] = useState<{ name: string, options: string, prices: { [key: string]: string } }[]>([]);
   const [existingCoverUrl, setExistingCoverUrl] = useState<string | null>(null);
   const [existingGalleryUrls, setExistingGalleryUrls] = useState<string[]>([]);

   // 1.1 ESTADO DO CROPPER
   const [cropperOpen, setCropperOpen] = useState(false);
   const [imageToCrop, setImageToCrop] = useState<string | null>(null);
   const [cropType, setCropType] = useState<'cover' | 'gallery'>('cover');

   // Carregar Categorias
   const loadCategories = async () => {
      try { const res = await axios.get(`${API_URL}/categories/${tenantId}`); setCategories(res.data); }
      catch (e) { console.error(e); }
   };

   useEffect(() => { loadCategories(); }, []);

   // Preencher dados ao Editar
   useEffect(() => {
      if (productToEdit) {
         setFormData({
            name: productToEdit.name,
            price: productToEdit.price,
            promotional_price: productToEdit.promotional_price || '',
            description: productToEdit.description || '',
            sku: productToEdit.sku || '',
            stock: productToEdit.stock || '',
            category_id: productToEdit.category_id || '',
            weight: productToEdit.weight || '',
            height: productToEdit.height || '',
            width: productToEdit.width || '',
            length: productToEdit.length || '',
            allow_backorder: Boolean(productToEdit.allow_backorder)
         });

         if (productToEdit.shipping_modes && Array.isArray(productToEdit.shipping_modes)) {
            setShippingModes(productToEdit.shipping_modes);
         } else if (productToEdit.shipping_mode) {
            if (productToEdit.shipping_mode === 'DELIVERY') setShippingModes(['ME_CORREIOS']);
            else setShippingModes([productToEdit.shipping_mode]);
         }

         if (productToEdit.attributes) {
            try {
               const attrs = typeof productToEdit.attributes === 'string' ? JSON.parse(productToEdit.attributes) : productToEdit.attributes;
               setAttributes(Object.entries(attrs).map(([k, v]) => ({ key: k, value: String(v) })));
            } catch { }
         }

         if (productToEdit.variations) {
            try {
               const v = typeof productToEdit.variations === 'string' ? JSON.parse(productToEdit.variations) : productToEdit.variations;
               if (Array.isArray(v)) setVariations(v.map((i: any) => ({
                  name: i.name,
                  options: Array.isArray(i.options) ? i.options.join(', ') : i.options,
                  prices: i.prices || {}
               })));
            } catch { }
         }

         // Popular imagens existentes ao editar
         setExistingCoverUrl(productToEdit.image_url || null);
         const imgs = typeof productToEdit.images === 'string' ? JSON.parse(productToEdit.images) : productToEdit.images;
         setExistingGalleryUrls(Array.isArray(imgs) ? imgs : []);

      } else {
         const autoSku = 'PROD-' + Date.now().toString().slice(-6);
         setFormData({ name: '', price: '', promotional_price: '', description: '', sku: autoSku, stock: '', category_id: '', weight: '', height: '', width: '', length: '', allow_backorder: false });
         setShippingModes(['ME_CORREIOS']);
         setAttributes([]);
         setVariations([]);
         setExistingCoverUrl(null);
         setExistingGalleryUrls([]);
      }
   }, [productToEdit]);

   const handleModeToggle = (mode: string) => {
      if (shippingModes.includes(mode)) {
         setShippingModes(shippingModes.filter(m => m !== mode));
      } else {
         setShippingModes([...shippingModes, mode]);
      }
   };

   const updateVariation = (idx: number, field: string, value: any) => {
      const newVars = [...variations];
      (newVars[idx] as any)[field] = value;
      setVariations(newVars);
   };

   const updateOptionConfig = (idx: number, option: string, field: string, value: any) => {
      const newVars = [...variations];
      const optName = option.trim();
      const currentConfig = newVars[idx].prices[optName];

      // Retrocompatibilidade: Se for string (legado), converte para objeto silenciosamente
      let updatedConfig = typeof currentConfig === 'string'
         ? { price: currentConfig, stock: '', imageIndex: '' }
         : { ...(currentConfig || { price: '', stock: '', imageIndex: '' }) };

      (updatedConfig as any)[field] = value;

      newVars[idx].prices = { ...newVars[idx].prices, [optName]: updatedConfig };
      setVariations(newVars);
   };

   const handleAddCategory = async () => {
      const name = prompt("Nome da Nova Categoria:");
      if (name) {
         await axios.post(`${API_URL}/categories`, { tenant_id: tenantId, name });
         loadCategories();
      }
   };

   // HANDLERS DE IMAGEM COM CROP
   const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>, type: 'cover' | 'gallery') => {
      const file = e.target.files?.[0];
      if (file) {
         const reader = new FileReader();
         reader.onload = () => {
            setImageToCrop(reader.result as string);
            setCropType(type);
            setCropperOpen(true);
         };
         reader.readAsDataURL(file);
         e.target.value = ''; // Reset input to allow re-selection
      }
   };

   const handleCropComplete = (croppedFile: File) => {
      if (cropType === 'cover') {
         setCoverFile(croppedFile);
      } else {
         setGalleryFiles(prev => [...prev, croppedFile]);
      }
      setCropperOpen(false);
      setImageToCrop(null);
   };

   const handleSubmit = async (e: React.FormEvent) => {
      // --- VALIDAÇÃO DE FRETE ---
      if (shippingModes.includes("ME_CORREIOS") || shippingModes.includes("MOTOBOY")) {
         const w = parseFloat(String(formData.weight || "0"));
         const h = parseFloat(String(formData.height || "0"));
         const wd = parseFloat(String(formData.width || "0"));
         const l = parseFloat(String(formData.length || "0"));
         if (w <= 0 || h <= 0 || wd <= 0 || l <= 0) {
            alert("⚠️ ATENÇÃO: Para envio via Correios ou Motoboy, os campos PESO e DIMENSÕES são obrigatórios!");
            setLoading(false);
            return;
         }
      }
      e.preventDefault(); setLoading(true);
      try {
         const data = new FormData();
         data.append('tenant_id', tenantId);

         // Anexar campos simples (convertendo null/undefined para string vazia se necessário)
         Object.keys(formData).forEach(key => {
            const val = (formData as any)[key];
            data.append(key, val === null || val === undefined ? '' : val);
         });

         // JSONs
         data.append('shipping_modes', JSON.stringify(shippingModes));

         if (coverFile) data.append('image', coverFile);
         if (galleryFiles) {
            for (let i = 0; i < galleryFiles.length; i++) {
               data.append('gallery', galleryFiles[i]);
            }
         }
         // --- NOVO: Enviar imagens existentes (não deletadas) ---
         if (productToEdit) {
            data.append('existingCoverUrl', existingCoverUrl || '');
            data.append('existingGalleryUrls', JSON.stringify(existingGalleryUrls));
         }

         const attrsObj: any = {}; attributes.forEach(a => { if (a.key) attrsObj[a.key] = a.value });
         data.append('attributes', JSON.stringify(attrsObj));

         const varsFinal = variations.map(v => ({
            name: v.name,
            options: v.options.split(',').map(s => s.trim()).filter(s => s),
            prices: v.prices
         })).filter(v => v.name && v.options.length > 0);
         data.append('variations', JSON.stringify(varsFinal));

         const url = productToEdit ? `${API_URL}/products/${productToEdit.id}` : `${API_URL}/products`;

         // Envio
         if (productToEdit) {
            await axios.put(url, data);
         } else {
            await axios.post(url, data);
         }

         onSuccess();
         onClose();
      } catch (err: any) {
         console.error(err);
         alert(err.response?.data?.error || 'Erro ao salvar produto. Verifique os campos.');
      }
      finally { setLoading(false); }
   };

   return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
         <div className="bg-white w-full max-w-4xl rounded-2xl shadow-2xl flex flex-col max-h-[90vh]">

            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
               <h3 className="text-xl font-bold text-gray-800">{productToEdit ? 'Editar Produto' : 'Novo Produto'}</h3>
               <button onClick={onClose}><X className="text-gray-400 hover:text-gray-600" /></button>
            </div>

            {/* Form Scrollable */}
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-8">

               {/* BLOCO 1: Informações Principais */}
               <div className="grid md:grid-cols-2 gap-6">
                  {/* Coluna Esquerda: Dados Básicos */}
                  <div className="space-y-4">
                     <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Nome do Produto</label>
                        <input required className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-emerald-500 outline-none"
                           value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} />
                     </div>

                     <div className="grid grid-cols-2 gap-4">
                        <div>
                           <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Preço (R$)</label>
                           <input required type="number" step="0.01" className="w-full border border-gray-300 rounded-lg p-2"
                              value={formData.price} onChange={e => setFormData({ ...formData, price: e.target.value })} />
                        </div>
                        <div>
                           <label className="block text-xs font-bold text-emerald-600 uppercase mb-1">Preço Promo</label>
                           <input type="number" step="0.01" className="w-full border border-emerald-200 bg-emerald-50 text-emerald-700 rounded-lg p-2"
                              value={formData.promotional_price} onChange={e => setFormData({ ...formData, promotional_price: e.target.value })} />
                        </div>
                     </div>

                     {/* SKU e Estoque */}
                     <div className="grid grid-cols-2 gap-4 bg-gray-50 p-3 rounded-lg border border-gray-200">
                        <div>
                           <label className="block text-xs font-bold text-gray-500 uppercase mb-1">SKU</label>
                           <input className="w-full border border-gray-300 rounded-lg p-2 bg-white"
                              value={formData.sku} onChange={e => setFormData({ ...formData, sku: e.target.value })} />
                        </div>

                        <div>
                           <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Estoque</label>
                           <input type="number" className="w-full border border-gray-300 rounded-lg p-2 bg-white"
                              value={formData.stock} onChange={e => setFormData({ ...formData, stock: e.target.value })} />

                           {/* Checkbox Aceitar Encomendas */}
                           <label className="flex items-center gap-2 mt-2 cursor-pointer select-none">
                              <div className={`w-4 h-4 rounded border flex items-center justify-center transition ${formData.allow_backorder ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-300'}`}>
                                 {formData.allow_backorder && <Check size={12} strokeWidth={3} />}
                                 <input type="checkbox" className="hidden"
                                    checked={formData.allow_backorder}
                                    onChange={e => setFormData({ ...formData, allow_backorder: e.target.checked })} />
                              </div>
                              <span className="text-[10px] font-bold text-gray-600 uppercase">Aceitar encomendas</span>
                           </label>
                        </div>
                     </div>

                     <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Categoria</label>
                        <div className="flex gap-2">
                           <select className="w-full border border-gray-300 rounded-lg p-2"
                              value={formData.category_id} onChange={e => setFormData({ ...formData, category_id: e.target.value })}>
                              <option value="">Sem Categoria</option>
                              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                           </select>
                           <button type="button" onClick={handleAddCategory} className="bg-blue-100 text-blue-600 p-2 rounded-lg hover:bg-blue-200"><Plus size={20} /></button>
                        </div>
                     </div>
                  </div>

                  {/* Coluna Direita: Descrição e IMAGENS */}
                  <div className="space-y-4">
                     <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Descrição</label>
                        <textarea className="w-full border border-gray-300 rounded-lg p-2" rows={4}
                           value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} />
                     </div>
                     <div className="grid grid-cols-2 gap-4">
                        {/* Capa */}
                        <div className="border-2 border-dashed border-gray-300 rounded-xl p-2 text-center hover:bg-gray-50 relative h-32 flex flex-col items-center justify-center transition overflow-hidden group">
                           <input type="file" accept="image/*" className="absolute inset-0 opacity-0 cursor-pointer z-10"
                              onChange={e => handleImageSelect(e, 'cover')} />

                           {coverFile ? (
                              <>
                                 <img src={URL.createObjectURL(coverFile)} className="w-full h-full object-contain absolute inset-0 rounded-xl bg-white" />
                                 <div className="absolute top-2 right-2 flex gap-2 z-30 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(URL.createObjectURL(coverFile), '_blank'); }} className="bg-blue-500 text-white rounded-full p-1.5 shadow-md hover:bg-blue-600">
                                       <Search size={14} />
                                    </button>
                                    <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setCoverFile(null); }} className="bg-red-500 text-white rounded-full p-1.5 shadow-md hover:bg-red-600">
                                       <X size={14} />
                                    </button>
                                 </div>
                              </>
                           ) : existingCoverUrl ? (
                              <>
                                 <img src={existingCoverUrl.startsWith('/uploads') ? `/api${existingCoverUrl}` : existingCoverUrl} className="w-full h-full object-contain absolute inset-0 rounded-xl bg-white" />
                                 <div className="absolute top-2 right-2 flex gap-2 z-30 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(existingCoverUrl.startsWith('/uploads') ? `/api${existingCoverUrl}` : existingCoverUrl, '_blank'); }} className="bg-blue-500 text-white rounded-full p-1.5 shadow-md hover:bg-blue-600">
                                       <Search size={14} />
                                    </button>
                                    <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setExistingCoverUrl(null); }} className="bg-red-500 text-white rounded-full p-1.5 shadow-md hover:bg-red-600">
                                       <X size={14} />
                                    </button>
                                 </div>
                              </>
                           ) : (
                              <>
                                 <Image size={24} className="text-gray-400 mb-1" />
                                 <span className="text-xs font-bold text-gray-500">Foto de Capa</span>
                                 <span className="text-[10px] text-gray-400 mt-1">Sugerido: 1:1 (800x800)</span>
                              </>
                           )}
                           {(coverFile || existingCoverUrl) && (
                              <span className="absolute bottom-1 left-0 right-0 text-center text-xs font-bold text-white bg-black/40 py-0.5 z-20 pointer-events-none">Trocar Capa</span>
                           )}
                        </div>
                        {/* Galeria Unificada */}
                        <div className="border-2 border-dashed border-blue-300 bg-blue-50 rounded-xl p-2 text-center hover:bg-blue-100 relative min-h-[8rem] flex flex-col items-center justify-center transition overflow-hidden group">
                           <input type="file" multiple accept="image/*" className="absolute inset-0 opacity-0 cursor-pointer z-10"
                              onChange={e => handleImageSelect(e, 'gallery')} />

                           {(galleryFiles.length > 0 || existingGalleryUrls.length > 0) ? (
                              <div className="flex gap-2 flex-wrap justify-center relative z-20 w-full h-full items-center p-2 pointer-events-none">

                                 {/* 1. Fotos Existentes do Banco (pointer-events-auto para poder clicar no X) */}
                                 {existingGalleryUrls.map((url, i) => (
                                    <div key={`ext-${i}`} className="relative group/item w-12 h-12 rounded-lg overflow-hidden border border-gray-200 shadow-sm pointer-events-auto flex items-center justify-center bg-white">
                                       <img src={url.startsWith('/uploads') ? `/api${url}` : url} className="w-full h-full object-contain" />
                                       <div className="absolute inset-0 bg-black/50 flex items-center justify-center gap-2 opacity-0 group-hover/item:opacity-100 transition-opacity z-30">
                                          <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(url.startsWith('/uploads') ? `/api${url}` : url, '_blank'); }} className="text-white hover:text-blue-300">
                                             <Search size={14} />
                                          </button>
                                          <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setExistingGalleryUrls(prev => prev.filter((_, idx) => idx !== i)); }} className="text-white hover:text-red-400">
                                             <X size={14} />
                                          </button>
                                       </div>
                                    </div>
                                 ))}

                                 {/* 2. Novas Fotos Adicionadas Agora (pointer-events-auto para poder clicar no X) */}
                                 {galleryFiles.map((f, i) => (
                                    <div key={`new-${i}`} className="relative group/item w-12 h-12 rounded-lg overflow-hidden border border-green-400 shadow-sm pointer-events-auto flex items-center justify-center bg-white">
                                       <img src={URL.createObjectURL(f)} className="w-full h-full object-contain" />
                                       <div className="absolute inset-0 bg-black/50 flex items-center justify-center gap-2 opacity-0 group-hover/item:opacity-100 transition-opacity z-30">
                                          <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(URL.createObjectURL(f), '_blank'); }} className="text-white hover:text-blue-300">
                                             <Search size={14} />
                                          </button>
                                          <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setGalleryFiles(prev => prev.filter((_, idx) => idx !== i)); }} className="text-white hover:text-red-400">
                                             <X size={14} />
                                          </button>
                                       </div>
                                    </div>
                                 ))}

                                 {/* Botão de Adicionar (+) dentro da lista (pointer-events-none para atravessar o clique) */}
                                 <div className="w-12 h-12 rounded-lg border-2 border-dashed border-blue-400 flex items-center justify-center text-blue-500 bg-white shadow-sm hover:bg-blue-50 relative pointer-events-none">
                                    <Plus size={20} />
                                 </div>
                              </div>
                           ) : (
                              <>
                                 <Images size={24} className="text-blue-500 mb-1" />
                                 <span className="text-xs font-bold text-blue-600">Galeria (+)</span>
                                 <span className="text-[10px] text-gray-500 mt-1 px-2">Clique ou arraste para adicionar fotos</span>
                              </>
                           )}

                           {(galleryFiles.length > 0 || existingGalleryUrls.length > 0) && (
                              <span className="absolute bottom-0 left-0 right-0 text-center text-[10px] font-bold text-white bg-black/50 py-0.5 z-20 pointer-events-none">
                                 {galleryFiles.length + existingGalleryUrls.length} Fotos (Clique no + para mais)
                              </span>
                           )}
                        </div>

                     </div>
                  </div>
               </div>

               {/* 2. VARIAÇÕES (MOVIDO PARA CIMA) */}
               <section className="bg-gray-50 border border-gray-200 rounded-xl p-5">
                  <div className="flex justify-between items-center mb-4">
                     <h4 className="font-bold text-gray-700 flex items-center gap-2"><Tag size={18} /> Variações (Cor, Tamanho)</h4>
                     <button type="button" onClick={() => setVariations([...variations, { name: '', options: '', prices: {} }])} className="text-xs bg-white border px-3 py-1 rounded-lg font-bold hover:bg-gray-100">+ Adicionar</button>
                  </div>

                  <div className="space-y-4">
                     {variations.map((v, idx) => (
                        <div key={idx} className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                           <div className="flex gap-4 mb-3">
                              <input placeholder="Nome (Ex: Cor)" className="w-1/3 border p-2 rounded-lg text-sm" value={v.name} onChange={e => updateVariation(idx, 'name', e.target.value)} />
                              <input placeholder="Opções (Ex: Azul, Vermelho)" className="flex-1 border p-2 rounded-lg text-sm" value={v.options} onChange={e => updateVariation(idx, 'options', e.target.value)} />
                              <button type="button" onClick={() => { const n = [...variations]; n.splice(idx, 1); setVariations(n) }} className="text-red-500 p-2"><Trash2 size={16} /></button>
                           </div>
                           {v.options && (
                              <details className="mt-4 group space-y-3 border-t border-gray-100 pt-4">
                                 <summary className="text-[10px] font-black text-emerald-600 hover:text-emerald-700 uppercase tracking-wider cursor-pointer list-none flex items-center gap-1 select-none transition-colors [&::-webkit-details-marker]:hidden">
                                    <span className="group-open:hidden flex items-center gap-1">▶ Configurar Preço, Estoque e Foto</span>
                                    <span className="hidden group-open:flex items-center gap-1 text-gray-400">▼ Esconder Configurações</span>
                                 </summary>
                                 <div className="mt-4 space-y-3">
                                    {v.options.split(',').filter(o => o.trim()).map(opt => {
                                       const optName = opt.trim();
                                       const config = v.prices[optName];

                                       // Leitura segura garantindo a compatibilidade com produtos antigos
                                       const priceValue = typeof config === 'string' ? config : (config?.price || '');
                                       const stockValue = typeof config === 'object' ? (config?.stock || '') : '';
                                       const imgIdx = typeof config === 'object' ? (config?.imageIndex || '') : '';

                                       // Calcula o total de fotos disponíveis na galeria dinamicamente
                                       const totalGalleryImgs = (galleryFiles ? galleryFiles.length : 0) + existingGalleryUrls.length;

                                       return (
                                          <div key={optName} className="grid grid-cols-1 sm:grid-cols-4 gap-3 bg-gray-50 p-3 rounded-xl border border-gray-200 shadow-sm items-end">
                                             <div className="sm:col-span-1 flex items-center h-full">
                                                <span className="text-sm font-bold text-gray-700 truncate">{optName}</span>
                                             </div>
                                             <div>
                                                <label className="text-[9px] font-bold text-gray-400 uppercase mb-1 block">Preço (R$)</label>
                                                <input type="number" step="0.01" placeholder="Usar Padrão" className="w-full text-sm border border-gray-300 p-2 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none transition bg-white"
                                                   value={priceValue} onChange={e => updateOptionConfig(idx, optName, 'price', e.target.value)} />
                                             </div>
                                             <div>
                                                <label className="text-[9px] font-bold text-gray-400 uppercase mb-1 block">Estoque</label>
                                                <input type="number" placeholder="Usar Global" className="w-full text-sm border border-gray-300 p-2 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none transition bg-white"
                                                   value={stockValue} onChange={e => updateOptionConfig(idx, optName, 'stock', e.target.value)} />
                                             </div>
                                             <div>
                                                <label className="text-[9px] font-bold text-gray-400 uppercase mb-1 block">Foto (Opcional)</label>
                                                <select className="w-full text-sm border border-gray-300 p-2 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none transition bg-white"
                                                   value={imgIdx} onChange={e => updateOptionConfig(idx, optName, 'imageIndex', e.target.value)}>
                                                   <option value="">Nenhuma</option>
                                                   <option value="cover">Foto de Capa</option>
                                                   {Array.from({ length: totalGalleryImgs }).map((_, i) => (
                                                      <option key={i} value={i}>Galeria: Foto {i + 1}</option>
                                                   ))}
                                                </select>
                                             </div>
                                          </div>
                                       );
                                    })}
                                 </div>
                              </details>
                           )}

                        </div>
                     ))}
                  </div>
               </section>

               {/* 3. LOGÍSTICA E FRETE (AGORA ABAIXO E COM CHECKBOXES INTUITIVOS) */}
               <section className="bg-orange-50 border border-orange-100 rounded-xl p-5">
                  <h4 className="font-bold text-orange-800 flex items-center gap-2 mb-4"><Truck size={18} /> Opções de Entrega Habilitadas</h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                     <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition ${shippingModes.includes('ME_CORREIOS') ? 'bg-orange-100 border-orange-300 ring-1 ring-orange-300' : 'bg-white border-orange-200'}`}>
                        <input type="checkbox" className="w-5 h-5 text-orange-600" checked={shippingModes.includes('ME_CORREIOS')} onChange={() => handleModeToggle('ME_CORREIOS')} />
                        <div>
                           <span className="block text-sm font-bold text-gray-800">Correios / Melhor Envio</span>
                           <span className="block text-xs text-gray-500">Calcula frete automático pelo CEP</span>
                        </div>
                     </label>

                     <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition ${shippingModes.includes('MOTOBOY') ? 'bg-orange-100 border-orange-300 ring-1 ring-orange-300' : 'bg-white border-orange-200'}`}>
                        <input type="checkbox" className="w-5 h-5 text-orange-600" checked={shippingModes.includes('MOTOBOY')} onChange={() => handleModeToggle('MOTOBOY')} />
                        <div>
                           <span className="block text-sm font-bold text-gray-800">Delivery Local (Motoboy)</span>
                           <span className="block text-xs text-gray-500">Para entregas na cidade (Rastreio manual)</span>
                        </div>
                     </label>

                     <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition ${shippingModes.includes('PICKUP') ? 'bg-orange-100 border-orange-300 ring-1 ring-orange-300' : 'bg-white border-orange-200'}`}>
                        <input type="checkbox" className="w-5 h-5 text-orange-600" checked={shippingModes.includes('PICKUP')} onChange={() => handleModeToggle('PICKUP')} />
                        <div>
                           <span className="block text-sm font-bold text-gray-800">Retirada no Local</span>
                           <span className="block text-xs text-gray-500">Cliente busca na loja (Grátis)</span>
                        </div>
                     </label>

                     <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition ${shippingModes.includes('DIGITAL') ? 'bg-orange-100 border-orange-300 ring-1 ring-orange-300' : 'bg-white border-orange-200'}`}>
                        <input type="checkbox" className="w-5 h-5 text-orange-600" checked={shippingModes.includes('DIGITAL')} onChange={() => handleModeToggle('DIGITAL')} />
                        <div>
                           <span className="block text-sm font-bold text-gray-800">Produto Digital</span>
                           <span className="block text-xs text-gray-500">Envio automático por Email/WhatsApp</span>
                        </div>
                     </label>
                  </div>

                  {/* Se selecionou Correios ou Motoboy, pede dimensões */}
                  {(shippingModes.includes('ME_CORREIOS') || shippingModes.includes('MOTOBOY')) && (
                     <div className="grid grid-cols-4 gap-4 animate-fade-in bg-white p-4 rounded-lg border border-orange-100">
                        <div><label className="text-[10px] font-bold uppercase text-gray-500">Peso (Kg)</label><input type="number" step="0.001" placeholder="0.500" className="w-full border p-2 rounded-lg" value={formData.weight} onChange={e => setFormData({ ...formData, weight: e.target.value })} /></div>
                        <div><label className="text-[10px] font-bold uppercase text-gray-500">Altura (cm)</label><input type="number" placeholder="10" className="w-full border p-2 rounded-lg" value={formData.height} onChange={e => setFormData({ ...formData, height: e.target.value })} /></div>
                        <div><label className="text-[10px] font-bold uppercase text-gray-500">Largura (cm)</label><input type="number" placeholder="20" className="w-full border p-2 rounded-lg" value={formData.width} onChange={e => setFormData({ ...formData, width: e.target.value })} /></div>
                        <div><label className="text-[10px] font-bold uppercase text-gray-500">Comp. (cm)</label><input type="number" placeholder="30" className="w-full border p-2 rounded-lg" value={formData.length} onChange={e => setFormData({ ...formData, length: e.target.value })} /></div>
                     </div>
                  )}
               </section>

            </form>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3">
               <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg">Cancelar</button>
               <button onClick={handleSubmit} disabled={loading} className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold shadow-lg shadow-emerald-600/20 flex items-center gap-2">
                  {loading ? 'Salvando...' : <><Save size={18} /> Salvar Produto</>}
               </button>
            </div>

            {cropperOpen && imageToCrop && (
               <ImageCropperModal
                  image={imageToCrop}
                  onClose={() => { setCropperOpen(false); setImageToCrop(null); }}
                  onCropComplete={handleCropComplete}
                  aspect={undefined}
               />
            )}

         </div>
      </div>
   );
};

// --- COMPONENTE PRINCIPAL (ATUALIZADO COM PAGINAÇÃO E FILTROS) ---
const AdminProducts = () => {
   const [activeTab, setActiveTab] = useState<'products' | 'categories'>('products');
   const [products, setProducts] = useState<any[]>([]);
   const [categories, setCategories] = useState<any[]>([]);
   const [categoriesList, setCategoriesList] = useState<any[]>([]); // Lista simples para o select de filtro

   const [loading, setLoading] = useState(true);

   // --- ESTADOS GERAIS DE PAGINAÇÃO ---
   const [searchTerm, setSearchTerm] = useState('');
   const [debouncedSearch, setDebouncedSearch] = useState('');
   const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('all'); // Apenas para produtos

   // Paginação separada para cada aba? Ou resetamos ao trocar? 
   // Melhor resetar currentPage ao trocar de aba, mas manter o estado limpo.
   const [currentPage, setCurrentPage] = useState(1);
   const [paginationMeta, setPaginationMeta] = useState({ totalItems: 0, totalPages: 1, itemsPerPage: 10 });

   const [productModalOpen, setProductModalOpen] = useState(false);
   const [editingProduct, setEditingProduct] = useState<any>(null);
   const [categoryModalOpen, setCategoryModalOpen] = useState(false);
   const [editingCategory, setEditingCategory] = useState<any>(null);

   const user = getAuth();

   useEffect(() => {
      const handler = setTimeout(() => {
         setDebouncedSearch(searchTerm);
         setCurrentPage(1);
      }, 500);
      return () => clearTimeout(handler);
   }, [searchTerm]);

   // Resetar paginação ao trocar de aba
   useEffect(() => {
      setSearchTerm('');
      setDebouncedSearch('');
      setCurrentPage(1);
      setProducts([]);
      setCategories([]);
   }, [activeTab]);

   const loadData = async () => {
      if (!user?.tenant_id) return;
      try {
         setLoading(true);

         // Carrega lista simples de categorias para o dropdown de filtro de produtos
         if (activeTab === 'products' && categoriesList.length === 0) {
            const catRes = await axios.get(`${API_URL}/categories/${user.tenant_id}`);
            setCategoriesList(catRes.data);
         }

         if (activeTab === 'products') {
            const params = new URLSearchParams({
               page: currentPage.toString(), limit: '10', search: debouncedSearch, category_id: selectedCategoryFilter
            });
            const res = await axios.get(`${API_URL}/admin/store/${user.tenant_id}/products?${params.toString()}`);
            setProducts(res.data.data);
            setPaginationMeta(res.data.meta);
         }
         else if (activeTab === 'categories') {
            const params = new URLSearchParams({
               page: currentPage.toString(), limit: '10', search: debouncedSearch
            });
            const res = await axios.get(`${API_URL}/admin/store/${user.tenant_id}/categories?${params.toString()}`);
            setCategories(res.data.data);
            setPaginationMeta(res.data.meta);
         }

      } catch (e) { console.error(e); }
      finally { setLoading(false); }
   };

   useEffect(() => { loadData(); }, [user?.tenant_id, activeTab, currentPage, debouncedSearch, selectedCategoryFilter]);

   const handleDeleteProduct = async (id: string) => {
      if (confirm('Excluir produto?')) { await axios.delete(`${API_URL}/products/${id}`); loadData(); }
   };
   const handleDeleteCategory = async (id: string) => {
      if (confirm('Excluir categoria?')) { await axios.delete(`${API_URL}/categories/${id}`, { data: { tenant_id: user.tenant_id } }); loadData(); }
   };

   return (
      <div className="space-y-6">
         <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex bg-gray-100 p-1 rounded-xl w-full sm:w-auto">
               <button onClick={() => setActiveTab('products')} className={`flex-1 sm:flex-none px-4 py-2 rounded-lg text-xs sm:text-sm font-bold flex items-center justify-center gap-2 transition ${activeTab === 'products' ? 'bg-white text-emerald-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}><Package size={18} /> Produtos</button>
               <button onClick={() => setActiveTab('categories')} className={`flex-1 sm:flex-none px-4 py-2 rounded-lg text-xs sm:text-sm font-bold flex items-center justify-center gap-2 transition ${activeTab === 'categories' ? 'bg-white text-emerald-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}><Layers size={18} /> Categorias</button>
            </div>
            <button onClick={() => { if (activeTab === 'products') { setEditingProduct(null); setProductModalOpen(true); } else { setEditingCategory(null); setCategoryModalOpen(true); } }} className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2.5 rounded-xl font-bold flex items-center justify-center gap-2 shadow-lg shadow-emerald-600/20 active:scale-95 transition-all"><Plus size={20} /> {activeTab === 'products' ? 'Novo Produto' : 'Nova Categoria'}</button>
         </div>

         {/* BARRA DE FILTROS COMUM */}
         <div className="bg-white p-3 rounded-xl shadow-sm border border-gray-100 flex flex-col md:flex-row gap-3 items-center">
            <div className="flex-1 w-full relative">
               <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
               <input type="text" placeholder={activeTab === 'products' ? "Buscar produto..." : "Buscar categoria..."} className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500/20 outline-none text-sm" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
            </div>
            {activeTab === 'products' && (
               <div className="w-full md:w-64">
                  <select className="w-full p-2.5 border border-gray-200 rounded-lg text-sm text-gray-600 bg-gray-50" value={selectedCategoryFilter} onChange={e => { setSelectedCategoryFilter(e.target.value); setCurrentPage(1); }}>
                     <option value="all">Todas as Categorias</option>
                     {categoriesList.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
               </div>
            )}
         </div>

         {loading ? <div className="p-10 text-center text-gray-500">Carregando...</div> : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">

               <div className="divide-y divide-gray-100">
                  {activeTab === 'products' && (
                     <>
                        {/* DESKTOP TABLE */}
                        <div className="hidden md:block overflow-x-auto">
                           <table className="w-full text-left">
                              <thead className="bg-gray-50 text-gray-600 text-xs uppercase font-bold"><tr><th className="p-4">Produto</th><th className="p-4">Preço</th><th className="p-4">Estoque</th><th className="p-4 text-right">Ações</th></tr></thead>
                              <tbody className="divide-y divide-gray-100">
                                 {products.map(p => (
                                    <tr key={p.id} className="hover:bg-gray-50 transition">
                                       <td className="p-4 flex items-center gap-3">
                                          <div className="w-10 h-10 bg-white border border-gray-100 rounded-lg overflow-hidden shrink-0 flex items-center justify-center">{p.image_url ? <img src={p.image_url.startsWith('/uploads') ? `/api${p.image_url}` : p.image_url} className="w-full h-full object-contain" /> : <Box className="m-2 text-gray-400" />}</div>
                                          <div><p className="font-bold text-gray-900 line-clamp-1">{p.name}</p><p className="text-xs text-gray-500">{p.sku || '-'}</p></div>
                                       </td>
                                       <td className="p-4">{p.promotional_price ? <div><span className="line-through text-xs text-gray-400">R${p.price}</span> <span className="font-bold text-emerald-600 block">R${p.promotional_price}</span></div> : <span className="font-medium">R$ {p.price}</span>}</td>
                                       <td className="p-4"><span className={`px-2 py-1 rounded text-xs font-bold ${p.stock > 0 ? 'bg-blue-50 text-blue-700' : 'bg-red-50 text-red-700'}`}>{p.stock} un {p.allow_backorder && <span className="ml-1 text-[10px] bg-blue-100 text-blue-700 px-1 rounded border border-blue-200">ENC</span>}</span></td>
                                       <td className="p-4 text-right flex justify-end gap-2"><button onClick={() => { setEditingProduct(p); setProductModalOpen(true); }} className="p-2 text-blue-600 hover:bg-blue-50 rounded"><Edit2 size={18} /></button><button onClick={() => handleDeleteProduct(p.id)} className="p-2 text-red-600 hover:bg-red-50 rounded"><Trash2 size={18} /></button></td>
                                    </tr>
                                 ))}
                              </tbody>
                           </table>
                        </div>

                        {/* MOBILE CARDS */}
                        <div className="md:hidden divide-y divide-gray-100">
                           {products.length === 0 ? <div className="p-10 text-center text-gray-400">Nenhum produto encontrado.</div> : products.map(p => (
                              <div key={p.id} className="p-4 flex flex-col gap-3">
                                 <div className="flex gap-3">
                                    <div className="w-14 h-14 bg-white rounded-xl overflow-hidden shrink-0 border border-gray-100 flex items-center justify-center">
                                       {p.image_url ? <img src={p.image_url.startsWith('/uploads') ? `/api${p.image_url}` : p.image_url} className="w-full h-full object-contain" /> : <Box className="p-3 text-gray-300 w-full h-full" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                       <p className="font-bold text-gray-900 text-sm truncate">{p.name}</p>
                                       <p className="text-[10px] text-gray-400 font-mono mb-1">{p.sku || 'SEM SKU'}</p>
                                       <div className="flex items-center gap-2">
                                          <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase ${p.stock > 0 ? 'bg-blue-50 text-blue-600' : 'bg-red-50 text-red-600'}`}>Estoque: {p.stock}</span>
                                          {p.allow_backorder && <span className="text-[10px] bg-amber-50 text-amber-600 px-2 py-0.5 rounded font-black border border-amber-100">ENC</span>}
                                       </div>
                                    </div>
                                    <div className="flex flex-col gap-2">
                                       <button onClick={() => { setEditingProduct(p); setProductModalOpen(true); }} className="p-2 bg-blue-50 text-blue-600 rounded-lg"><Edit2 size={16} /></button>
                                       <button onClick={() => handleDeleteProduct(p.id)} className="p-2 bg-red-50 text-red-600 rounded-lg"><Trash2 size={16} /></button>
                                    </div>
                                 </div>
                                 <div className="flex justify-between items-center bg-gray-50 p-2 rounded-lg">
                                    <span className="text-[10px] font-bold text-gray-400 uppercase">Preço</span>
                                    <div className="text-right">
                                       {p.promotional_price ? <div className="flex items-center gap-2"><span className="line-through text-[10px] text-gray-400">R${p.price}</span><span className="text-sm font-black text-emerald-600">R${p.promotional_price}</span></div> : <span className="text-sm font-black text-gray-700">R$ {p.price}</span>}
                                    </div>
                                 </div>
                              </div>
                           ))}
                        </div>
                     </>
                  )}

                  {activeTab === 'categories' && (
                     <div className="divide-y divide-gray-100">
                        {categories.length === 0 ? <div className="p-10 text-center text-gray-400">Nenhuma categoria encontrada.</div> : categories.map(c => (
                           <div key={c.id} className="p-4 flex items-center justify-between hover:bg-gray-50 transition">
                              <div className="flex items-center gap-3">
                                 <div className="w-10 h-10 bg-emerald-50 rounded-full flex items-center justify-center text-emerald-600"><Tag size={18} /></div>
                                 <div>
                                    <p className="font-bold text-gray-800 text-sm">{c.name}</p>
                                    <p className="text-[10px] text-gray-400 font-bold uppercase">{c.product_count || 0} Itens</p>
                                 </div>
                              </div>
                              <div className="flex gap-2">
                                 <button onClick={() => { setEditingCategory(c); setCategoryModalOpen(true); }} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"><Edit2 size={18} /></button>
                                 <button onClick={() => handleDeleteCategory(c.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 size={18} /></button>
                              </div>
                           </div>
                        ))}
                     </div>
                  )}
               </div>

               {/* PAGINAÇÃO GLOBAL (SERVE PARA AS DUAS ABAS) */}
               <div className="p-4">
                  <Pagination
                     currentPage={currentPage}
                     totalPages={paginationMeta.totalPages}
                     onPageChange={setCurrentPage}
                     totalItems={paginationMeta.totalItems}
                     itemsPerPage={paginationMeta.itemsPerPage}
                  />
               </div>
            </div>
         )}

         {productModalOpen && <ProductModal tenantId={user.tenant_id} productToEdit={editingProduct} onClose={() => setProductModalOpen(false)} onSuccess={loadData} />}
         {categoryModalOpen && <CategoryModal tenantId={user.tenant_id} categoryToEdit={editingCategory} onClose={() => setCategoryModalOpen(false)} onSuccess={loadData} />}
      </div>
   );
};

export default AdminProducts;
